import { useEffect, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { Button } from './ui/button';

const heroImages = [
  "/lovable-uploads/79655136-70e9-412b-8184-29f5b6ac839a.png",
  "/lovable-uploads/51faa35d-e40b-4ed5-9a14-d0d120a568ef.png",
  "/lovable-uploads/30f7d289-7f40-492d-a43e-067039e55a9a.png",
  "/lovable-uploads/4756aca8-d4fb-4d0c-8b5c-c1e79f83fc85.png",
  "/lovable-uploads/cde0e67c-3c3d-4c4d-b7fb-07693620ea15.png",
  "/lovable-uploads/b344527b-b00a-4ad2-9619-4d8ad4266f26.png",
  "/lovable-uploads/d15a03a1-51c8-4bff-9b2e-974fdd1221b5.png",
  "/lovable-uploads/42b9dfe6-bf98-4011-95ad-5638e1ebbe14.png",
  "/lovable-uploads/9c1a048a-fad2-4df3-9093-d289183387d1.png",
  "/lovable-uploads/6eb81cf8-dd23-45a0-897e-38e657560145.png",
  "/lovable-uploads/7c80076f-c773-42dd-8a94-414d5c75e0e2.png",
  "/lovable-uploads/da370836-12d5-4821-9ebd-23de5cfa6360.png",
  "/lovable-uploads/c3886631-1446-48ed-8572-791f204cdc71.png",
  "/lovable-uploads/59218433-7cec-4d0e-a6ab-4ba3727e9ba3.png",
  "/lovable-uploads/78609e45-c9a0-45f6-bc8b-e2041d986218.png",
  "/lovable-uploads/c8b3a9f3-598e-46ca-a363-d126f2d84a71.png"
];

const preloadImage = (src: string) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.src = src;
    img.onload = resolve;
    img.onerror = reject;
  });
};

const Hero = () => {
  const titleRef = useRef<HTMLHeadingElement>(null);
  const subtitleRef = useRef<HTMLParagraphElement>(null);
  const ctaRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const [visibleImages, setVisibleImages] = useState<string[]>([]);

  const handleContactFormScroll = () => {
    const contactForm = document.getElementById('contact');
    if (contactForm) {
      contactForm.scrollIntoView({ behavior: 'smooth' });
    }
  };

  useEffect(() => {
    const preloadBatchOfImages = async () => {
      try {
        const initialBatch = heroImages.slice(0, 3);
        await Promise.all(initialBatch.map(src => preloadImage(src)));
        setVisibleImages(initialBatch);
        setImagesLoaded(true);
        
        setTimeout(async () => {
          const remainingImages = heroImages.slice(3);
          await Promise.all(remainingImages.map(src => preloadImage(src)));
          setVisibleImages(heroImages);
        }, 2000);
      } catch (error) {
        console.error('Failed to preload images:', error);
        setVisibleImages(heroImages);
        setImagesLoaded(true);
      }
    };
    
    preloadBatchOfImages();
  }, []);

  useEffect(() => {
    const title = titleRef.current;
    const subtitle = subtitleRef.current;
    const cta = ctaRef.current;
    const scroll = scrollRef.current;

    if (title) title.classList.add('animate-fade-in-up');
    
    setTimeout(() => {
      if (subtitle) subtitle.classList.add('animate-fade-in-up');
    }, 200);

    setTimeout(() => {
      if (cta) cta.classList.add('animate-fade-in-up');
    }, 500);

    setTimeout(() => {
      if (scroll) scroll.classList.add('animate-fade-in');
    }, 1200);

    const imageInterval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => 
        prevIndex === visibleImages.length - 1 ? 0 : prevIndex + 1
      );
    }, 3500);

    return () => clearInterval(imageInterval);
  }, [visibleImages.length]);

  return (
    <section 
      id="hero" 
      className="relative min-h-screen flex items-center overflow-hidden w-full"
    >
      {!imagesLoaded && (
        <div className="absolute inset-0 bg-elegant-100 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-navy-800"></div>
        </div>
      )}

      {visibleImages.map((image, index) => (
        <div 
          key={image}
          className={`absolute inset-0 w-full h-full transition-opacity duration-1500 ease-in-out bg-cover bg-center bg-no-repeat ${
            index === currentImageIndex ? 'opacity-100' : 'opacity-0'
          }`}
          style={{
            backgroundImage: `url("${image}")`,
          }}
          aria-hidden={index !== currentImageIndex}
        />
      ))}

      <div className="container-custom relative z-10 py-20 md:py-32 w-full">
        <div className="max-w-3xl mx-auto text-center mb-16">
          <h1 
            ref={titleRef}
            className="font-serif text-2xl md:text-3xl lg:text-4xl text-elegant-900 mb-6 opacity-0"
          >
            Tworzymy, bo wierzymy, że wspomnienia<br />
            są ważne. Z biegiem lat stają się cenniejsze.
          </h1>
          
          <p 
            ref={subtitleRef}
            className="text-base md:text-lg text-elegant-900 mb-12 max-w-2xl mx-auto opacity-0"
          >
            Naturalna fotografia. Klasyczne filmy ślubne.
          </p>
          
          <div ref={ctaRef} className="flex flex-col sm:flex-row justify-center gap-4 mt-10 opacity-0">
            <Button 
              asChild 
              variant="default"
              className="bg-navy-800 hover:bg-navy-700 text-white max-w-[200px] sm:max-w-[180px] mx-auto sm:mx-0 w-auto"
            >
              <a href="#about">Poznaj Czułe klisze</a>
            </Button>
            
            <Button 
              variant="outline" 
              className="bg-transparent border border-navy-800 text-navy-800 hover:bg-navy-800 hover:text-white transition-all duration-300 max-w-[200px] sm:max-w-[180px] mx-auto sm:mx-0 w-auto"
              onClick={handleContactFormScroll}
            >
              Formularz Kontaktowy
            </Button>
          </div>
        </div>
      </div>

      <div 
        ref={scrollRef}
        className="absolute bottom-12 left-1/2 transform -translate-x-1/2 z-10 opacity-0 animate-pulse-slow"
      >
        <a 
          href="#about" 
          className="flex flex-col items-center text-elegant-900 hover:text-navy-800 transition-colors"
        >
          <span className="text-sm uppercase tracking-widest mb-2">Przewiń</span>
          <ChevronDown size={20} />
        </a>
      </div>
    </section>
  );
};

export default Hero;
